import { ComponentType } from 'react';
import {
  AppointmentBookingSkill,
  validateAppointmentBookingConfig,
} from './AppointmentBookingSkill';
import {
  CallBackLaterSkill,
  validateCallBackLaterConfig,
} from './CallBackLaterSkill';
import { DispatchSkill, validateDispatchConfig } from './DispatchSkill';
import { EndCallSkill, validateEndCallConfig } from './EndCallSkill';
import {
  ExternalApiSkill,
  validateExternalApiConfig,
} from './ExternalApiSkill';
import {
  GateKeeperHandlingSkill,
  validateGateKeeperHandlingConfig,
} from './GateKeeperHandlingSkill';
import {
  InCallEmailSkill,
  validateInCallEmailConfig,
} from './InCallEmailSkill';
import { InCallSmsSkill, validateInCallSmsConfig } from './InCallSmsSkill';
import {
  IvrNavigationSkill,
  validateIvrNavigationConfig,
} from './IvrNavigationSkill';
import {
  KnowledgeSearchSkill,
  validateKnowledgeSearchConfig,
} from './KnowledgeSearchSkill';
import {
  SwitchCommunicationSkill,
  validateSwitchCommunicationConfig,
} from './SwitchCommunicationSkill';

export enum SkillType {
  BOOK_APPOINTMENT = 'book_appointment',
  DISPATCH_ON_PHRASE = 'dispatch_on_phrase',
  CALL_BACK_LATER = 'call_back_later',
  ECOMMERCE_SHOPPING = 'ecommerce_shopping',
  KNOWLEDGE_SEARCH = 'knowledge_search',
  SWITCH_COMMUNICATION = 'switch_communication',
  IN_CALL_SMS = 'in_call_sms',
  END_CALL = 'end_call',
  IVR_NAVIGATION = 'ivr_navigation',
  EXTERNAL_API = 'external_api',
  GATE_KEEPER_HANDLING = 'gate_keeper_handling',
  IN_CALL_EMAIL = 'in_call_email',
}

export const REQUIRED_AGENT_CONFIG_FOR_SKILLS = [
  {
    type: SkillType.BOOK_APPOINTMENT,
    config: {
      enable_mcp: true,
    },
  },
  {
    type: SkillType.DISPATCH_ON_PHRASE,
    config: {
      enable_mcp: true,
    },
  },
];

const defaultValidator = (config: any): boolean => {
  return false;
};

export const SKILL_OPTIONS: Record<
  string,
  {
    type: SkillType;
    name: string;
    tooltip: string;
    component: ComponentType<{
      config: any;
      onConfigChange?: (config: any) => void;
    }>;
    validator: (config: any) => boolean;
    experimental?: boolean;
  }
> = {
  [SkillType.BOOK_APPOINTMENT]: {
    type: SkillType.BOOK_APPOINTMENT,
    name: 'Appointment Scheduling',
    tooltip: 'Schedule appointments with your customers',
    component: AppointmentBookingSkill,
    validator: validateAppointmentBookingConfig,
  },
  [SkillType.DISPATCH_ON_PHRASE]: {
    type: SkillType.DISPATCH_ON_PHRASE,
    name: 'Call Routing',
    tooltip: 'Transfer to a human based on a certain phrase',
    component: DispatchSkill,
    validator: validateDispatchConfig,
  },
  [SkillType.CALL_BACK_LATER]: {
    type: SkillType.CALL_BACK_LATER,
    name: 'Call Back Later',
    tooltip:
      'Automatically calls your customer back if they’re unavailable right now.',
    component: CallBackLaterSkill,
    validator: validateCallBackLaterConfig,
  },
  [SkillType.KNOWLEDGE_SEARCH]: {
    type: SkillType.KNOWLEDGE_SEARCH,
    name: 'Knowledge Search',
    tooltip: 'Search your knowledge base for information.',
    component: KnowledgeSearchSkill,
    validator: validateKnowledgeSearchConfig,
  },
  [SkillType.SWITCH_COMMUNICATION]: {
    type: SkillType.SWITCH_COMMUNICATION,
    name: 'Switch Communication',
    tooltip: 'Allow switching to a different communication channel',
    component: SwitchCommunicationSkill,
    validator: validateSwitchCommunicationConfig,
  },
  [SkillType.IN_CALL_SMS]: {
    type: SkillType.IN_CALL_SMS,
    name: 'In Call SMS',
    tooltip: 'Send SMS messages during a call',
    component: InCallSmsSkill,
    validator: validateInCallSmsConfig,
  },
  [SkillType.END_CALL]: {
    type: SkillType.END_CALL,
    name: 'End Call',
    tooltip: 'Allows the agent to end the call',
    component: EndCallSkill,
    validator: validateEndCallConfig,
  },
  [SkillType.IVR_NAVIGATION]: {
    type: SkillType.IVR_NAVIGATION,
    name: 'IVR Navigation',
    tooltip: 'Navigate through an IVR menu to accomplish a goal',
    component: IvrNavigationSkill,
    validator: validateIvrNavigationConfig,
  },
  [SkillType.EXTERNAL_API]: {
    type: SkillType.EXTERNAL_API,
    name: 'External API',
    tooltip: 'Call an external API',
    component: ExternalApiSkill,
    validator: validateExternalApiConfig,
  },
  [SkillType.GATE_KEEPER_HANDLING]: {
    type: SkillType.GATE_KEEPER_HANDLING,
    name: 'Call Screen Handling',
    tooltip: 'Gracefully handle call assistants and receptionists',
    component: GateKeeperHandlingSkill,
    validator: validateGateKeeperHandlingConfig,
  },
  [SkillType.IN_CALL_EMAIL]: {
    type: SkillType.IN_CALL_EMAIL,
    name: 'In Call Email',
    tooltip: 'Send emails during a call',
    component: InCallEmailSkill,
    validator: validateInCallEmailConfig,
    experimental: true,
  },
};

export type CallBackLaterConfig = {
  callback_initial_message?: string;
  end_call_message?: string;
};

export const INITIAL_CALL_TIME_CONFIRMATION_MESSAGE =
  "Great. We'll call you back at that time. Goodbye.";

const INITIAL_CALL_BACK_LATER_CONFIG: CallBackLaterConfig = {
  end_call_message: INITIAL_CALL_TIME_CONFIRMATION_MESSAGE,
};

export const SKILL_DEFAULT_CONFIG: Record<string, object> = {
  [SkillType.CALL_BACK_LATER]: INITIAL_CALL_BACK_LATER_CONFIG,
};
